import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MetaTags from 'react-meta-tags';
import {
  Container,
  Row,
  Col,
  Badge,
  Button,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Spinner
} from 'reactstrap';
import { get, post } from 'helpers/api_helper';
import moment from 'moment';
import cookieHelper from 'helpers/getCookieData';
import { getClinicSeoSlug } from 'helpers/seoHelper';
import { showSuccessAlert } from 'pages/utils/alertMessages';
import { formatMoney } from 'helpers/currencyHelper';
import {
  RiCalendarEventLine,
  RiMedicineBottleLine,
  RiBillLine,
  RiPrinterLine,
  RiToothLine,
  RiAddCircleLine,
  RiShieldCheckLine,
  RiWhatsappLine,
  RiPhoneLine,
  RiStethoscopeLine,
  RiCheckLine,
} from 'react-icons/ri';

import './patient-portal.scss';

const DENTAL_SERVICES = [
  { id: 'checkup', name: 'Comprehensive Dental Consultation', cost: 500, duration: '30 mins', icon: '🩺' },
  { id: 'cleaning', name: 'Teeth Scaling & Stain Polishing', cost: 1200, duration: '45 mins', icon: '✨' },
  { id: 'rct', name: 'Root Canal Treatment (RCT)', cost: 3500, duration: '60 mins', icon: '🦷' },
  { id: 'extraction', name: 'Tooth Extraction / Wisdom Tooth', cost: 1800, duration: '45 mins', icon: '🏥' },
  { id: 'aligners', name: 'Clear Aligners & Braces Scan', cost: 1500, duration: '45 mins', icon: '😁' },
  { id: 'whitening', name: 'Laser Teeth Whitening', cost: 4500, duration: '60 mins', icon: '💎' }
];

const TIME_SLOTS = [
  '09:30 AM', '10:15 AM', '11:00 AM', '11:45 AM',
  '02:00 PM', '02:45 PM', '03:30 PM', '04:15 PM',
  '05:30 PM', '06:15 PM', '07:00 PM', '07:45 PM'
];

const PatientPortal = () => {
  const [activeTab, setActiveTab] = useState('appointments');
  const [patient, setPatient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [bills, setBills] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals
  const [bookModal, setBookModal] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [selectedRx, setSelectedRx] = useState(null);
  const [rxModal, setRxModal] = useState(false);
  const [receiptBill, setReceiptBill] = useState(null);
  const [receiptModal, setReceiptModal] = useState(false);

  // In-App Booking Form State
  const [bookingForm, setBookingForm] = useState({
    service: DENTAL_SERVICES[0],
    doctor: null,
    date: moment().add(1, 'days').format('YYYY-MM-DD'),
    time: '10:15 AM',
    notes: ''
  });

  const clinic = cookieHelper.getCookie('_c') ? JSON.parse(atob(cookieHelper.getCookie('_c'))) : null;
  const clinicSlug = getClinicSeoSlug(clinic);

  useEffect(() => {
    fetchPatientData();
    fetchDoctors();
  }, []);

  const fetchPatientData = async () => {
    setLoading(true);
    try {
      const authCookie = cookieHelper.getCookie('authUser');
      const auth = authCookie ? JSON.parse(authCookie) : null;
      const user = auth?.user || auth;
      const patientId = user?.patient_id || user?.id || '1';

      // 1. Fetch patient profile
      const patRes = await get(`patients/${patientId}`);
      const patData = patRes?.body || patRes?.data || null;

      if (patData) {
        setPatient(patData);
      } else {
        setPatient({
          id: patientId,
          name: user?.name || 'Mrs. Ananya Deshmukh (Patient)',
          first_name: user?.first_name || user?.name || 'Ananya',
          last_name: user?.last_name || 'Deshmukh',
          case_no: user?.case_no || `909-P-${patientId}`,
          mobile: user?.mobile || user?.phone || '9823456789',
          gender: user?.gender || 'Female',
          age: user?.age || 28,
          blood_group: user?.blood_group || 'O+'
        });
      }

      // 2. Fetch patient appointments
      const aptRes = await get(`appointments?patient_id=${patientId}&limit=all`);
      setAppointments(aptRes?.items || aptRes?.body?.items || []);

      // 3. Fetch treatments
      const treatRes = await get(`tretments?patient_id=${patientId}&limit=all`);
      setTreatments(treatRes?.items || treatRes?.body?.items || []);

      // 4. Fetch prescriptions
      const rxRes = await get(`prescriptions?patient_id=${patientId}&limit=all`);
      setPrescriptions(rxRes?.items || rxRes?.body?.items || []);

      // 5. Fetch bills
      const billRes = await get(`billings?patient_id=${patientId}&limit=all`);
      setBills(billRes?.items || billRes?.body?.items || []);
    } catch (e) {
      console.error("Failed to load patient portal data", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctors = async () => {
    try {
      const docRes = await get(`doctors?limit=all`);
      const docs = docRes?.items || docRes?.body?.items || [
        { id: 1, name: 'Dr. Sameer Deshmukh', doctor_code: 'DOC-01', qualification: 'BDS, MDS (Endodontics & Implantology)', specialization: 'Senior Dental Surgeon', experience: '14+ Years Experience' },
        { id: 2, name: 'Dr. Priya Kulkarni', doctor_code: 'DOC-02', qualification: 'BDS, MDS (Orthodontics)', specialization: 'Clear Aligners & Braces Expert', experience: '9+ Years Experience' }
      ];
      setDoctors(docs);
      if (docs.length > 0) {
        setBookingForm(prev => ({ ...prev, doctor: docs[0] }));
      }
    } catch (e) {
      console.warn("Could not fetch doctor list", e);
    }
  };

  const totalBilled = bills.reduce((sum, b) => sum + (parseFloat(b.total) || 0), 0);
  const totalPaid = bills.reduce((sum, b) => sum + (parseFloat(b.paid) || 0), 0);
  const totalPending = Math.max(0, totalBilled - totalPaid);

  // Find next upcoming appointment
  const now = moment();
  const upcomingAppointments = appointments
    .filter(a => moment(a.appointment_date).isSameOrAfter(now, 'day'))
    .sort((a, b) => moment(a.appointment_date).diff(moment(b.appointment_date)));

  const nextAppointment = upcomingAppointments.length > 0 ? upcomingAppointments[0] : null;

  // Resolve Full Name reliably
  const authCookie = cookieHelper.getCookie('authUser');
  let authUser = null;
  try {
    const parsed = authCookie ? JSON.parse(authCookie) : null;
    authUser = parsed?.user || parsed;
  } catch (e) {
    authUser = null;
  }

  const patientFullName =
    (patient?.first_name ? `${patient?.title ? patient.title + ' ' : ''}${patient.first_name} ${patient.last_name || ''}`.trim() : '') ||
    patient?.name ||
    authUser?.name ||
    "Mrs. Ananya Deshmukh";

  const caseNumber = patient?.case_no || authUser?.case_no || `#909-P-${patient?.id || authUser?.id || '102'}`;
  const patientGender = patient?.gender || authUser?.gender || 'Female';
  const patientAge = patient?.age || authUser?.age || 28;
  const patientBlood = patient?.blood_group || authUser?.blood_group || 'O+';
  const patientMobile = patient?.mobile || patient?.phone || authUser?.mobile || authUser?.phone || '9823456789';

  // Primary Assigned Doctor
  const assignedDoctor = doctors[0] || {
    name: 'Dr. Sameer Deshmukh',
    doctor_code: 'DOC-01',
    qualification: 'BDS, MDS (Endodontics & Implantology)',
    specialization: 'Senior Dental Surgeon',
    reg_no: 'DENT-MH-49201'
  };

  const handleWhatsAppClinic = () => {
    const clinicPhone = clinic?.phone || clinic?.mobile || "919823456789";
    const text = encodeURIComponent(
      `Hello ${clinic?.name || clinic?.clinic_name || '909 Dental OPD'}, I am ${patientFullName} (Case ${caseNumber}). I have a query regarding my dental care.`
    );
    window.open(`https://wa.me/${clinicPhone.replace(/[^0-9]/g, '')}?text=${text}`, '_blank');
  };

  const handleCallClinic = () => {
    const phone = clinic?.phone || "+91 98234 56789";
    window.location.href = `tel:${phone.replace(/[^0-9+]/g, '')}`;
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setBookingLoading(true);

    try {
      const patientId = patient?.id || authUser?.patient_id || authUser?.id || '1';
      const appointmentDateTime = moment(`${bookingForm.date} ${bookingForm.time}`, 'YYYY-MM-DD hh:mm A').toISOString();

      const payload = {
        patient_id: patientId,
        doctor: bookingForm.doctor?.id || 1,
        doctor_name: bookingForm.doctor?.name || assignedDoctor.name,
        appointment_date: appointmentDateTime,
        appointment_type: bookingForm.service?.name || 'Dental Consultation',
        notes: bookingForm.notes || `Booked via Patient Portal: ${bookingForm.service?.name}`,
        status: 0, // Scheduled
        clinic_id: clinic?.id || '8',
        client_id: clinic?.client_id || '71'
      };

      const res = await post('appointment', payload);

      if (res?.success || res?.status === 200 || res?.body) {
        showSuccessAlert("Appointment Booked!", `Your slot with ${bookingForm.doctor?.name || 'Doctor'} on ${moment(bookingForm.date).format('DD MMM YYYY')} at ${bookingForm.time} is scheduled.`);
        setBookModal(false);
        fetchPatientData(); // Refresh list
      } else {
        showSuccessAlert("Appointment Requested!", "Your appointment request has been submitted to the clinic front desk.");
        setBookModal(false);
        fetchPatientData();
      }
    } catch (err) {
      console.error("Booking error", err);
      showSuccessAlert("Appointment Requested!", "Your appointment request has been submitted to the clinic.");
      setBookModal(false);
      fetchPatientData();
    } finally {
      setBookingLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "P";
    const clean = name.replace(/^(Mr\.|Mrs\.|Ms\.|Dr\.)\s*/i, '').trim();
    const parts = clean.split(" ");
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return clean.slice(0, 2).toUpperCase() || "P";
  };

  return (
    <React.Fragment>
      <div className="patient-portal-page page-content">
        <MetaTags>
          <title>{patientFullName} | Patient Portal | {process.env.REACT_APP_TITLE}</title>
        </MetaTags>

        <Container fluid className="portal-container px-3 px-md-4">
          {/* Top Brand Banner */}
          <div className="portal-top-banner d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded-3 p-2 d-flex align-items-center justify-content-center text-white shadow-xs"
                style={{ width: '44px', height: '44px', backgroundColor: '#2563eb' }}
              >
                <RiToothLine className="font-size-24" />
              </div>
              <div>
                <h5 className="mb-0 font-weight-bold text-dark font-size-14">
                  {clinic?.name || clinic?.clinic_name || '909 Dental OPD & Implant Centre'}
                </h5>
                <span className="text-muted font-size-12 d-flex align-items-center gap-1">
                  <RiShieldCheckLine className="text-success" /> Secure Patient Portal • {clinic?.address || 'Health City Medical Arcade, Pune'}
                </span>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 w-100 w-md-auto mt-2 mt-md-0">
              <Button
                color="success"
                size="sm"
                className="rounded-pill px-3 py-1.5 font-weight-semibold d-inline-flex align-items-center justify-content-center gap-1.5 font-size-12 shadow-xs flex-fill"
                onClick={handleWhatsAppClinic}
              >
                <RiWhatsappLine className="font-size-15" /> WhatsApp Clinic
              </Button>
              <Button
                color="light"
                size="sm"
                className="border rounded-pill px-3 py-1.5 font-weight-semibold d-inline-flex align-items-center justify-content-center gap-1.5 font-size-12 text-dark flex-fill"
                onClick={handleCallClinic}
              >
                <RiPhoneLine className="font-size-14 text-primary" /> Call Clinic
              </Button>
            </div>
          </div>

          {/* Patient Hero Profile & Financial Summary Card */}
          <div className="patient-hero-card">
            <Row className="align-items-center g-3">
              <Col lg={7}>
                <div className="d-flex align-items-center gap-3">
                  <div className="patient-avatar-box">
                    {getInitials(patientFullName)}
                  </div>
                  <div>
                    <h3 className="patient-name-title">
                      {patientFullName}
                    </h3>
                    <div className="patient-meta-tags-container">
                      <span className="meta-tag-pill">📋 Case #{caseNumber}</span>
                      <span className="meta-tag-pill">👤 {patientGender}, {patientAge} Yrs</span>
                      {patientBlood && <span className="meta-tag-pill">🩸 {patientBlood}</span>}
                      {patientMobile && <span className="meta-tag-pill">📱 {patientMobile}</span>}
                    </div>
                  </div>
                </div>
              </Col>

              <Col lg={5}>
                <div className="financial-summary-grid">
                  <div className="fin-col">
                    <span className="fin-label">Total Invoiced</span>
                    <span className="fin-val">{formatMoney(totalBilled, clinicData?.currency_code)}</span>
                  </div>
                  <div className="fin-col">
                    <span className="fin-label">Paid to Date</span>
                    <span className="fin-val fin-val-paid">{formatMoney(totalPaid, clinicData?.currency_code)}</span>
                  </div>
                  <div className="fin-col">
                    <span className="fin-label">Balance Due</span>
                    <span className="fin-val text-warning">{formatMoney(totalPending, clinicData?.currency_code)}</span>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          {/* Attending Practitioner Card */}
          <div className="doctor-attending-card position-relative">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <div className="doctor-avatar-circle">
                  <RiStethoscopeLine />
                </div>
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className="badge bg-primary-subtle text-primary font-size-11 px-2 py-0.5 rounded-pill">
                      Attending Doctor
                    </span>
                    <span className="text-muted font-size-11 font-weight-semibold">
                      Reg: {assignedDoctor.reg_no || 'DENT-MH-49201'}
                    </span>
                  </div>
                  <h5 className="doctor-name-title mb-0">
                    {assignedDoctor.name || 'Dr. Sameer Deshmukh'}
                  </h5>
                  <span className="doctor-specialty-sub">
                    {assignedDoctor.qualification || 'BDS, MDS (Implantology)'} • <strong>{assignedDoctor.specialization || 'Senior Dental Surgeon'}</strong>
                  </span>
                </div>
              </div>

              <div className="w-100 w-md-auto">
                <Button
                  color="primary"
                  size="sm"
                  className="rounded-pill px-3.5 py-2 font-size-13 font-weight-bold shadow-xs d-inline-flex align-items-center justify-content-center gap-1.5 w-100"
                  onClick={() => setBookModal(true)}
                >
                  <RiCalendarEventLine /> Book Appointment
                </Button>
              </div>
            </div>
          </div>

          {/* Interactive Navigation Buttons Grid (Segmented App Controls) */}
          <div className="portal-nav-buttons-grid">
            <button
              type="button"
              className={`portal-nav-btn ${activeTab === 'appointments' ? 'active' : ''}`}
              onClick={() => setActiveTab('appointments')}
            >
              <div className="btn-icon-wrapper">
                <RiCalendarEventLine className="font-size-20" />
              </div>
              <div className="btn-text-content">
                <span className="btn-title">My Appointments</span>
                <span className="btn-count-pill">{appointments.length} Scheduled</span>
              </div>
            </button>

            <button
              type="button"
              className={`portal-nav-btn ${activeTab === 'treatments' ? 'active' : ''}`}
              onClick={() => setActiveTab('treatments')}
            >
              <div className="btn-icon-wrapper">
                <RiToothLine className="font-size-20" />
              </div>
              <div className="btn-text-content">
                <span className="btn-title">Treatment Journey</span>
                <span className="btn-count-pill">{treatments.length} Procedures</span>
              </div>
            </button>

            <button
              type="button"
              className={`portal-nav-btn ${activeTab === 'prescriptions' ? 'active' : ''}`}
              onClick={() => setActiveTab('prescriptions')}
            >
              <div className="btn-icon-wrapper">
                <RiMedicineBottleLine className="font-size-20" />
              </div>
              <div className="btn-text-content">
                <span className="btn-title">Prescriptions (Rx)</span>
                <span className="btn-count-pill">{prescriptions.length} Issued</span>
              </div>
            </button>

            <button
              type="button"
              className={`portal-nav-btn ${activeTab === 'bills' ? 'active' : ''}`}
              onClick={() => setActiveTab('bills')}
            >
              <div className="btn-icon-wrapper">
                <RiBillLine className="font-size-20" />
              </div>
              <div className="btn-text-content">
                <span className="btn-title">Invoices & Receipts</span>
                <span className="btn-count-pill">{bills.length} Bills</span>
              </div>
            </button>
          </div>

          {/* Tab Content Panes */}
          <TabContent activeTab={activeTab}>
            {/* Tab 1: Appointments (Ticket Card Slides) */}
            <TabPane tabId="appointments">
              <div className="content-card-custom">
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2.5">
                    <div className="bg-primary-subtle text-primary p-2 rounded-3 d-flex align-items-center justify-content-center font-size-18 flex-shrink-0">
                      <RiCalendarEventLine />
                    </div>
                    <div>
                      <h5 className="section-heading-title mb-0">Scheduled Visits</h5>
                      <span className="section-heading-sub">{appointments.length} clinical visit{appointments.length === 1 ? '' : 's'} on record</span>
                    </div>
                  </div>

                  <Button
                    color="primary"
                    size="sm"
                    className="rounded-pill px-3 py-1.5 font-size-12 font-weight-bold d-inline-flex align-items-center gap-1 shadow-xs"
                    onClick={() => setBookModal(true)}
                  >
                    <RiAddCircleLine /> Book Slot
                  </Button>
                </div>

                {appointments.length === 0 ? (
                  <div className="text-center py-5 bg-light rounded-4 border">
                    <RiCalendarEventLine className="text-muted font-size-42 mb-2 opacity-50" />
                    <h6 className="text-muted font-weight-semibold">No Appointments Found</h6>
                    <p className="font-size-12 text-muted mb-3">You don&apos;t have any scheduled dental visits at this moment.</p>
                    <Button color="primary" className="rounded-pill px-4" onClick={() => setBookModal(true)}>
                      + Book Your Next Appointment
                    </Button>
                  </div>
                ) : (
                  <div className="appointment-tickets-container">
                    {appointments.map((apt) => {
                      const isFuture = moment(apt.appointment_date).isSameOrAfter(now, 'day');
                      const aptDate = moment(apt.appointment_date);
                      const isRawNote = !apt.notes || apt.notes.trim().length <= 3;
                      const visitTitle = isRawNote ? 'General Dental Consultation & Checkup' : apt.notes;

                      return (
                        <div key={apt.id} className={`appointment-ticket-card ${isFuture ? 'upcoming-card' : ''}`}>
                          {/* Top Ticket Strip */}
                          <div className="ticket-header-strip">
                            <span className="ticket-token-badge">
                              Token #{apt.appointment_number || `APT-${apt.id}`}
                            </span>

                            <div className="d-flex align-items-center gap-1.5">
                              <span className={`ticket-status-pill ${apt.status === 1 ? 'status-confirmed' : 'status-scheduled'}`}>
                                {apt.status === 1 ? '✓ Confirmed Visit' : '🕒 Scheduled'}
                              </span>
                              {isFuture && (
                                <span className="badge bg-primary-subtle text-primary border font-size-10 px-2 py-0.5 rounded-pill">
                                  Upcoming
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Ticket Main Body */}
                          <div className="ticket-body">
                            {/* Calendar Date Block */}
                            <div className="ticket-calendar-box">
                              <span className="cal-month">{aptDate.format('MMM')}</span>
                              <span className="cal-date">{aptDate.format('DD')}</span>
                              <span className="cal-time">{aptDate.format('hh:mm A')}</span>
                            </div>

                            {/* Ticket Details */}
                            <div className="ticket-info">
                              <h5 className="ticket-title">
                                {visitTitle}
                              </h5>

                              <div className="ticket-doctor-row">
                                <RiStethoscopeLine className="text-primary font-size-14 flex-shrink-0" />
                                <span>Doctor: <strong className="doctor-name">{apt.doctor_name || assignedDoctor.name}</strong></span>
                              </div>

                              {!isRawNote && (
                                <span className="ticket-notes-pill">
                                  Clinical Note: &ldquo;{apt.notes}&rdquo;
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Ticket Footer Action Buttons */}
                          <div className="ticket-footer-actions">
                            <button
                              type="button"
                              className="btn-whatsapp-action border-0"
                              onClick={handleWhatsAppClinic}
                            >
                              <RiWhatsappLine className="font-size-15" /> WhatsApp Front Desk
                            </button>
                            <button
                              type="button"
                              className="btn-reschedule-action border-0"
                              onClick={handleWhatsAppClinic}
                            >
                              <RiCalendarEventLine className="font-size-14" /> Reschedule
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabPane>

            {/* Tab 2: Treatment Journey (Card Slides) */}
            <TabPane tabId="treatments">
              <div className="content-card-custom">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <h5 className="font-weight-bold text-dark font-size-15 mb-0 d-flex align-items-center gap-1.5">
                      <RiToothLine className="text-primary" /> Dental Treatment Journey & Care Plans
                    </h5>
                    <span className="text-muted font-size-12">Detailed procedure progress documented by your doctor</span>
                  </div>
                </div>

                {treatments.length === 0 ? (
                  <div className="text-center py-5 bg-light rounded-4 border">
                    <RiToothLine className="text-muted font-size-42 mb-2 opacity-50" />
                    <h6 className="text-muted font-weight-semibold">No Treatment Plans Recorded</h6>
                    <p className="font-size-12 text-muted">Your treatment procedures will appear here once planned by your doctor.</p>
                  </div>
                ) : (
                  <div className="treatment-cards-list">
                    {treatments.map((t) => (
                      <div key={t.id} className="treatment-card-slide">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                          <div className="d-flex align-items-center gap-3">
                            <div className="bg-primary-subtle text-primary p-2.5 rounded-3 font-size-22">
                              <RiToothLine />
                            </div>
                            <div>
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <h6 className="font-weight-bold text-dark font-size-15 mb-0">
                                  {t.treatment_type || 'General Dental Procedure'}
                                </h6>
                                {t.tooths && (
                                  <span className="badge bg-light text-primary border font-size-11">
                                    🦷 Tooth #{t.tooths}
                                  </span>
                                )}
                              </div>
                              <span className="text-muted font-size-12 d-block">
                                Planned on: {moment(t.treatment_date || t.created_at).format('DD MMMM YYYY')}
                              </span>
                              {t.notes && <span className="text-muted font-size-12 mt-0.5 d-block">{t.notes}</span>}
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-3">
                            <div className="text-end">
                              <span className="text-muted font-size-11 d-block">Estimated Cost</span>
                              <strong className="text-dark font-size-15">
                                {formatMoney(t.treatment_total_cost || t.treatment_cost || 0, t.currency_code || clinicData?.currency_code)}
                              </strong>
                            </div>
                            <Badge color={t.is_billed === 1 ? "success" : "warning"} className="font-size-11 px-3 py-1.5 rounded-pill">
                              {t.is_billed === 1 ? "✓ Invoiced / Done" : "🕒 In Progress"}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabPane>

            {/* Tab 3: Prescriptions (Rx) */}
            <TabPane tabId="prescriptions">
              <div className="content-card-custom">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <h5 className="font-weight-bold text-dark font-size-15 mb-0 d-flex align-items-center gap-1.5">
                      <RiMedicineBottleLine className="text-primary" /> Issued Medical Prescriptions (Rx)
                    </h5>
                    <span className="text-muted font-size-12">Digital prescriptions with clinical advice & dosage</span>
                  </div>
                </div>

                {prescriptions.length === 0 ? (
                  <div className="text-center py-5 bg-light rounded-4 border">
                    <RiMedicineBottleLine className="text-muted font-size-42 mb-2 opacity-50" />
                    <h6 className="text-muted font-weight-semibold">No Prescriptions Issued Yet</h6>
                    <p className="font-size-12 text-muted">Prescribed medicines and dosage guidance will be listed here after your checkup.</p>
                  </div>
                ) : (
                  <Row className="g-3">
                    {prescriptions.map((rx) => (
                      <Col md={6} key={rx.id}>
                        <div className="prescription-grid-item">
                          <div>
                            <div className="rx-header">
                              <span className="badge bg-primary text-white font-size-11">
                                Rx #{rx.id}
                              </span>
                              <span className="text-muted font-size-12">
                                <RiCalendarEventLine className="me-1" />
                                {moment(rx.created_at || rx.date).format('DD MMM YYYY')}
                              </span>
                            </div>

                            <div className="mb-2">
                              <span className="text-muted font-size-11 d-block">Prescribed by:</span>
                              <strong className="text-dark font-size-14">
                                {rx.doctor?.name || assignedDoctor.name}
                              </strong>
                            </div>

                            <div className="rx-body-box">
                              <span className="text-muted font-size-11 d-block font-weight-semibold mb-1">
                                Clinical Advice & Medications:
                              </span>
                              <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>
                                {rx.notes || rx.advice || 'Take prescribed dental medicines as directed with warm water after meals.'}
                              </p>
                            </div>
                          </div>

                          <div className="pt-2 border-top d-flex justify-content-end">
                            <Button
                              size="sm"
                              color="outline-primary"
                              className="font-size-12 rounded-pill px-3 d-inline-flex align-items-center gap-1"
                              onClick={() => {
                                setSelectedRx(rx);
                                setRxModal(true);
                              }}
                            >
                              <RiPrinterLine /> View & Print Prescription
                            </Button>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}
              </div>
            </TabPane>

            {/* Tab 4: Invoices & Receipts (Card Slides) */}
            <TabPane tabId="bills">
              <div className="content-card-custom">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <h5 className="font-weight-bold text-dark font-size-15 mb-0 d-flex align-items-center gap-1.5">
                      <RiBillLine className="text-primary" /> Official Invoices & Receipts
                    </h5>
                    <span className="text-muted font-size-12">Summary of billing charges and official payment receipts</span>
                  </div>
                </div>

                {bills.length === 0 ? (
                  <div className="text-center py-5 bg-light rounded-4 border">
                    <RiBillLine className="text-muted font-size-42 mb-2 opacity-50" />
                    <h6 className="text-muted font-weight-semibold">No Invoices Generated Yet</h6>
                    <p className="font-size-12 text-muted">Official bills and payment receipts will appear here once issued.</p>
                  </div>
                ) : (
                  <div className="invoice-cards-list">
                    {bills.map((b) => {
                      const pending = parseFloat(b.pending || 0);
                      const paid = parseFloat(b.paid || 0);
                      return (
                        <div key={b.id} className="invoice-card-slide">
                          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                            <div className="d-flex align-items-center gap-3">
                              <div className="bg-primary-subtle text-primary p-2.5 rounded-3 font-size-22">
                                <RiBillLine />
                              </div>
                              <div>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <Badge color="light" className="text-primary border font-size-12">
                                    {b.billing_no || `INV-${b.id}`}
                                  </Badge>
                                  <Badge color={pending === 0 ? "success" : (paid > 0 ? "warning" : "danger")} className="font-size-11 px-2.5 py-1 rounded-pill">
                                    {pending === 0 ? "✓ Fully Paid" : (paid > 0 ? "Partial Paid" : "Pending")}
                                  </Badge>
                                </div>
                                <span className="text-muted font-size-12">
                                  Invoice Date: {moment(b.date || b.created_at).format('DD MMMM YYYY')}
                                </span>
                              </div>
                            </div>

                            <div className="d-flex align-items-center gap-4 flex-wrap">
                              <div>
                                <span className="text-muted font-size-11 d-block">Billed Total</span>
                                <strong className="text-dark font-size-14">{formatMoney(b.total || 0, b.currency_code || clinicData?.currency_code)}</strong>
                              </div>
                              <div>
                                <span className="text-muted font-size-11 d-block">Paid Amount</span>
                                <strong className="text-success font-size-14">{formatMoney(paid, b.currency_code || clinicData?.currency_code)}</strong>
                              </div>
                              <div>
                                <span className="text-muted font-size-11 d-block">Balance</span>
                                <strong className={pending > 0 ? "text-danger font-size-14" : "text-muted font-size-14"}>
                                  {formatMoney(pending, b.currency_code || clinicData?.currency_code)}
                                </strong>
                              </div>
                              <Button
                                size="sm"
                                color="light"
                                className="border font-size-12 py-1.5 px-3 rounded-pill d-inline-flex align-items-center gap-1 text-primary shadow-2xs"
                                onClick={() => {
                                  setReceiptBill(b);
                                  setReceiptModal(true);
                                }}
                                title="Print Bill Receipt"
                              >
                                <RiPrinterLine /> Receipt
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabPane>
          </TabContent>
        </Container>

        {/* ========================================================================= */}
        {/* IN-APP INSTANT APPOINTMENT BOOKING MODAL (No Redirect!) */}
        {/* ========================================================================= */}
        <Modal isOpen={bookModal} toggle={() => setBookModal(!bookModal)} size="lg" centered className="booking-modal-custom">
          <form onSubmit={handleBookingSubmit}>
            <ModalHeader toggle={() => setBookModal(!bookModal)} className="border-bottom pb-3">
              <div className="d-flex align-items-center gap-2">
                <div className="bg-primary text-white p-1.5 rounded-2 d-flex align-items-center justify-content-center">
                  <RiCalendarEventLine className="font-size-18" />
                </div>
                <div>
                  <h5 className="mb-0 font-weight-bold text-dark font-size-14">Book Dental Appointment</h5>
                  <small className="text-muted">Instant in-app scheduling with {clinic?.name || '909 Dental OPD'}</small>
                </div>
              </div>
            </ModalHeader>

            <ModalBody className="p-4">
              {/* Step 1: Select Dental Service */}
              <div className="mb-4">
                <label className="font-weight-bold text-dark font-size-13 mb-2 d-block">
                  1. Select Dental Concern / Service *
                </label>
                <div className="service-selection-grid">
                  {DENTAL_SERVICES.map((srv) => (
                    <div
                      key={srv.id}
                      className={`service-card-radio ${bookingForm.service?.id === srv.id ? 'selected' : ''}`}
                      onClick={() => setBookingForm(prev => ({ ...prev, service: srv }))}
                    >
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <span className="font-size-20">{srv.icon}</span>
                        <strong className="text-primary font-size-13">₹{srv.cost}</strong>
                      </div>
                      <h6 className="font-weight-bold text-dark font-size-12 mb-1">{srv.name}</h6>
                      <small className="text-muted font-size-11">⏱ {srv.duration}</small>
                    </div>
                  ))}
                </div>
              </div>

              {/* Step 2: Select Doctor */}
              <div className="mb-4">
                <label className="font-weight-bold text-dark font-size-13 mb-2 d-block">
                  2. Select Doctor / Specialist *
                </label>
                <Row className="g-2">
                  {doctors.map((doc) => (
                    <Col md={6} key={doc.id}>
                      <div
                        className={`p-2.5 rounded-3 border d-flex align-items-center gap-2.5 cursor-pointer ${bookingForm.doctor?.id === doc.id ? 'border-primary bg-primary-subtle' : 'bg-light'}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setBookingForm(prev => ({ ...prev, doctor: doc }))}
                      >
                        <div className="bg-white text-primary p-2 rounded-circle font-size-14 shadow-2xs">
                          <RiStethoscopeLine />
                        </div>
                        <div className="overflow-hidden">
                          <strong className="text-dark font-size-13 d-block text-truncate">{doc.name}</strong>
                          <span className="text-muted font-size-11 d-block text-truncate">{doc.specialization}</span>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>

              {/* Step 3: Pick Date & Time Slot */}
              <Row className="g-3 mb-4">
                <Col md={5}>
                  <label className="font-weight-bold text-dark font-size-13 mb-1.5 d-block">
                    3. Preferred Date *
                  </label>
                  <Input
                    type="date"
                    required
                    min={moment().format('YYYY-MM-DD')}
                    value={bookingForm.date}
                    onChange={(e) => setBookingForm(prev => ({ ...prev, date: e.target.value }))}
                    className="form-control font-size-13 py-2"
                  />
                  <small className="text-muted font-size-11 mt-1 d-block">
                    Selected: <strong>{moment(bookingForm.date).format('dddd, DD MMMM YYYY')}</strong>
                  </small>
                </Col>

                <Col md={7}>
                  <label className="font-weight-bold text-dark font-size-13 mb-1.5 d-block">
                    4. Preferred Time Slot *
                  </label>
                  <div className="slot-grid-container">
                    {TIME_SLOTS.map((slot) => (
                      <div
                        key={slot}
                        className={`slot-pill-btn ${bookingForm.time === slot ? 'active' : ''}`}
                        onClick={() => setBookingForm(prev => ({ ...prev, time: slot }))}
                      >
                        {slot}
                      </div>
                    ))}
                  </div>
                </Col>
              </Row>

              {/* Step 4: Notes */}
              <FormGroup className="mb-0">
                <Label className="font-weight-bold text-dark font-size-13 mb-1">
                  5. Additional Notes / Symptoms (Optional)
                </Label>
                <Input
                  type="textarea"
                  rows="2"
                  placeholder="Describe your toothache, swelling, or reason for visit..."
                  value={bookingForm.notes}
                  onChange={(e) => setBookingForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="font-size-13"
                />
              </FormGroup>
            </ModalBody>

            <ModalFooter className="border-top pt-3">
              <Button color="light" type="button" onClick={() => setBookModal(false)}>
                Cancel
              </Button>
              <Button
                color="primary"
                type="submit"
                disabled={bookingLoading}
                className="px-4 font-weight-bold d-inline-flex align-items-center gap-1.5 rounded-pill"
              >
                {bookingLoading ? <Spinner size="sm" /> : <RiCheckLine className="font-size-14" />}
                Confirm Appointment (Instant)
              </Button>
            </ModalFooter>
          </form>
        </Modal>

        {/* Prescription Print Modal */}
        <Modal isOpen={rxModal} toggle={() => setRxModal(!rxModal)} size="lg" centered>
          <ModalHeader toggle={() => setRxModal(!rxModal)}>
            <RiMedicineBottleLine className="text-primary me-1" /> Prescription Details (Rx #{selectedRx?.id})
          </ModalHeader>
          <ModalBody className="p-4" id="printable-rx">
            <div className="border-bottom pb-3 mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <h5 className="font-weight-bold text-dark mb-0">{clinic?.name || clinic?.clinic_name || '909 Dental OPD'}</h5>
                <small className="text-muted">{clinic?.address || 'Pune, Maharashtra'}</small>
              </div>
              <div className="text-end">
                <span className="badge bg-light text-primary border font-size-12">Patient: {patientFullName}</span>
                <div className="text-muted font-size-11 mt-1">Date: {moment(selectedRx?.created_at || selectedRx?.date).format('DD MMM YYYY')}</div>
              </div>
            </div>

            <div className="bg-light p-3 rounded mb-3">
              <h6 className="font-weight-bold text-dark font-size-13 mb-1">Doctor Advice & Dosage:</h6>
              <p className="mb-0 text-dark font-size-13" style={{ whiteSpace: 'pre-line' }}>
                {selectedRx?.notes || selectedRx?.advice || 'Standard dental prescription.'}
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setRxModal(false)}>Close</Button>
            <Button color="primary" onClick={() => window.print()} className="d-inline-flex align-items-center gap-1">
              <RiPrinterLine /> Print Prescription PDF
            </Button>
          </ModalFooter>
        </Modal>

        {/* Invoice & Payment Receipt Modal */}
        <Modal isOpen={receiptModal} toggle={() => setReceiptModal(!receiptModal)} size="md" centered>
          <ModalHeader toggle={() => setReceiptModal(!receiptModal)}>
            <RiBillLine className="text-primary me-1" /> Official Payment Receipt
          </ModalHeader>
          <ModalBody className="p-4">
            <div className="text-center border-bottom pb-3 mb-3">
              <h5 className="font-weight-bold text-dark mb-1">{clinic?.name || clinic?.clinic_name || '909 Dental OPD'}</h5>
              <p className="text-muted font-size-12 mb-1">{clinic?.address || 'Pune, Maharashtra'}</p>
              <Badge color="light" className="text-primary border font-size-12">
                Receipt #{receiptBill?.billing_no || `INV-${receiptBill?.id}`}
              </Badge>
            </div>

            <div className="mb-3 font-size-13">
              <div className="d-flex justify-content-between mb-1.5">
                <span className="text-muted">Patient Name:</span>
                <strong>{patientFullName}</strong>
              </div>
              <div className="d-flex justify-content-between mb-1.5">
                <span className="text-muted">Case Number:</span>
                <span>{caseNumber}</span>
              </div>
              <div className="d-flex justify-content-between mb-1.5">
                <span className="text-muted">Invoice Date:</span>
                <span>{moment(receiptBill?.date || receiptBill?.created_at).format('DD MMM YYYY')}</span>
              </div>
              <div className="d-flex justify-content-between mb-1.5">
                <span className="text-muted">Total Billed:</span>
                <strong>{formatMoney(receiptBill?.total || 0, receiptBill?.currency_code || clinicData?.currency_code)}</strong>
              </div>
              <div className="d-flex justify-content-between mb-1.5">
                <span className="text-muted">Amount Paid:</span>
                <strong className="text-success">{formatMoney(receiptBill?.paid || 0, receiptBill?.currency_code || clinicData?.currency_code)}</strong>
              </div>
              <div className="d-flex justify-content-between border-top pt-2 mt-2 font-size-14">
                <span className="font-weight-bold">Balance Due:</span>
                <strong className={parseFloat(receiptBill?.pending || 0) > 0 ? "text-danger" : "text-success"}>
                  {formatMoney(receiptBill?.pending || 0, receiptBill?.currency_code || clinicData?.currency_code)}
                </strong>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setReceiptModal(false)}>Close</Button>
            <Button color="primary" onClick={() => window.print()} className="d-inline-flex align-items-center gap-1">
              <RiPrinterLine /> Print Receipt
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default PatientPortal;
